const PHASES = [
    { key: "pahse_avoltage", current: "pahse_acurrent", label: "R", color: "#ef4444" },
    { key: "pahse_bvoltage", current: "pahse_bcurrent", label: "Y", color: "#eab308" },
    { key: "pahse_cvoltage", current: "pahse_ccurrent", label: "B", color: "#2563eb" },
];

function formatValue(value, unit = "") {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return `${value}${unit}`;
}

function isAlarm(status) {
    return Number(status) === 1;
}

function PhaseRow({ phase, rectifier, stopped }) {
    return (
        <div className={`rectifier-cabinet__phase ${stopped ? "is-stopped" : ""}`}>
            <span className="rectifier-cabinet__phase-tag" style={{ "--phase-color": phase.color }}>
                {phase.label}
            </span>
            <div className="rectifier-cabinet__phase-readout">
                <span>{formatValue(rectifier?.[phase.key], "V")}</span>
                <small>{formatValue(rectifier?.[phase.current], "A")}</small>
            </div>
            <span
                className={`rectifier-cabinet__phase-flow ${stopped ? "" : "is-live"}`}
                style={{ "--flow-color": phase.color }}
            />
        </div>
    );
}

function ModuleSlot({ index, fault }) {
    return (
        <div className={`rectifier-cabinet__module ${fault ? "is-fault" : "is-running"}`}>
            <span className="rectifier-cabinet__module-led" />
            <span className="rectifier-cabinet__module-id">M{index + 1}</span>
            <span className="rectifier-cabinet__module-bar" />
        </div>
    );
}

export default function RectifierSchematic({ rectifier, siteName, updateTime }) {
    const mainsFail = isAlarm(rectifier?.mains_fail);
    const dcLow = isAlarm(rectifier?.dc_low);
    const moduleFault = isAlarm(rectifier?.rectifier_fault);
    const llvd = isAlarm(rectifier?.llvd);
    const fuseFault = isAlarm(rectifier?.bat_load_fuse_fault);
    const modules = Number(rectifier?.num_module) || 0;
    const slotCount = Math.max(modules || 3, 3);
    const isLive = !rectifier?.is_disconnect;

    return (
        <div className="rectifier-schematic">
            <div className="rectifier-schematic__toolbar">
                <span className={`rectifier-schematic__status ${isLive ? "is-online" : ""}`}>
                    <span className={`rectifier-live-dot ${isLive ? "is-live" : ""}`} />
                    {isLive ? "Telemetry Online" : "Device Offline"}
                </span>
                <span className="rectifier-schematic__divider">|</span>
                <span className="rectifier-schematic__meta">{siteName || rectifier?.site_id}</span>
                <span className="rectifier-schematic__divider">|</span>
                <span className="rectifier-schematic__meta">{modules} Module{modules === 1 ? "" : "s"}</span>
                {updateTime ? (
                    <>
                        <span className="rectifier-schematic__divider">|</span>
                        <span className="rectifier-schematic__meta">{updateTime}</span>
                    </>
                ) : null}
            </div>

            <div className="rectifier-schematic__canvas">
                <div className="rectifier-cabinet">
                    <div className="rectifier-cabinet__chassis">
                        <div className="rectifier-cabinet__top-rail">
                            <span className="rectifier-cabinet__brand">{rectifier?.brand_name || "Rectifier Controller"}</span>
                            <span className="rectifier-cabinet__model">{rectifier?.name || "—"}</span>
                            <span className={`rectifier-cabinet__sys-led ${isLive ? "is-online" : "is-offline"}`}>
                                SYS
                            </span>
                        </div>

                        <div className="rectifier-cabinet__body">
                            <section className={`rectifier-cabinet__panel rectifier-cabinet__panel--ac ${mainsFail ? "is-alarm" : "is-ok"}`}>
                                <header className="rectifier-cabinet__panel-head">
                                    <i className="fa-light fa-plug-circle-bolt" />
                                    AC MAINS
                                </header>
                                <div className="rectifier-cabinet__lcd">
                                    <span className="rectifier-cabinet__lcd-label">Frequency</span>
                                    <strong>{formatValue(rectifier?.ac_frequency, " Hz")}</strong>
                                </div>
                                <div className="rectifier-cabinet__phases">
                                    {PHASES.map((phase) => (
                                        <PhaseRow
                                            key={phase.key}
                                            phase={phase}
                                            rectifier={rectifier}
                                            stopped={mainsFail}
                                        />
                                    ))}
                                </div>
                                <footer className={`rectifier-cabinet__status ${mainsFail ? "alarm" : "ok"}`}>
                                    {mainsFail ? "MAINS FAIL" : "STABLE"}
                                </footer>
                            </section>

                            <div className="rectifier-cabinet__bus rectifier-cabinet__bus--ac">
                                {PHASES.map((phase) => (
                                    <span
                                        key={phase.key}
                                        className={`rectifier-cabinet__bus-line ${mainsFail ? "" : "is-live"}`}
                                        style={{ "--flow-color": phase.color }}
                                    />
                                ))}
                            </div>

                            <section className={`rectifier-cabinet__panel rectifier-cabinet__panel--rack ${moduleFault ? "is-alarm" : "is-ok"}`}>
                                <header className="rectifier-cabinet__panel-head">
                                    <i className="fa-light fa-server" />
                                    RECTIFIER RACK
                                </header>
                                <div className="rectifier-cabinet__rack-grid">
                                    {Array.from({ length: Math.min(slotCount, 6) }).map((_, index) => (
                                        <ModuleSlot key={index} index={index} fault={moduleFault} />
                                    ))}
                                </div>
                                <footer className={`rectifier-cabinet__status ${moduleFault ? "alarm" : "ok"}`}>
                                    {moduleFault ? "MODULE FAULT" : `${modules || 0} ACTIVE`}
                                </footer>
                            </section>

                            <div className={`rectifier-cabinet__bus rectifier-cabinet__bus--dc ${dcLow ? "is-alarm" : "is-live"}`}>
                                <span>DC BUS</span>
                            </div>

                            <section className={`rectifier-cabinet__panel rectifier-cabinet__panel--dc ${dcLow ? "is-alarm" : "is-ok"}`}>
                                <header className="rectifier-cabinet__panel-head">
                                    <i className="fa-light fa-bolt" />
                                    DC OUTPUT
                                </header>
                                <div className="rectifier-cabinet__dc-display">
                                    <span className="rectifier-cabinet__dc-value">
                                        {formatValue(rectifier?.dc_voltage, "")}
                                    </span>
                                    <span className="rectifier-cabinet__dc-unit">V</span>
                                </div>
                                <div className="rectifier-cabinet__dc-meta">
                                    <span>I: {formatValue(rectifier?.dc_current, " A")}</span>
                                    <span>P: {formatValue(rectifier?.dc_active_power, " kW")}</span>
                                </div>
                                <footer className={`rectifier-cabinet__status ${dcLow ? "alarm" : "ok"}`}>
                                    {dcLow ? "DC LOW" : "NORMAL"}
                                </footer>
                            </section>

                            <div className={`rectifier-cabinet__bus rectifier-cabinet__bus--load ${llvd || fuseFault ? "is-alarm" : "is-live"}`} />

                            <section className={`rectifier-cabinet__panel rectifier-cabinet__panel--load ${fuseFault || llvd ? "is-alarm" : "is-ok"}`}>
                                <header className="rectifier-cabinet__panel-head">
                                    <i className="fa-light fa-tower-cell" />
                                    LOAD
                                </header>
                                <div className="rectifier-cabinet__lcd">
                                    <span className="rectifier-cabinet__lcd-label">Tenant Feed</span>
                                    <strong>{formatValue(rectifier?.cum_dc_power, " kWh")}</strong>
                                </div>
                                <footer className={`rectifier-cabinet__status ${llvd || fuseFault ? "alarm" : "ok"}`}>
                                    {llvd ? "LLVD" : fuseFault ? "FUSE FAULT" : "ONLINE"}
                                </footer>
                            </section>
                        </div>

                        <div className="rectifier-cabinet__bottom-rail">
                            <span>Controller ID: {rectifier?.rectifier_sl_no || "—"}</span>
                            <span>Site: {siteName || rectifier?.site_id || "—"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rectifier-schematic__legend">
                <span className="rectifier-schematic__legend-item">
                    <span className="legend-dot ok" aria-hidden="true" />
                    Normal
                </span>
                <span className="rectifier-schematic__legend-item">
                    <span className="legend-dot alarm" aria-hidden="true" />
                    Alarm
                </span>
                <span className="rectifier-schematic__legend-item">
                    <span className="legend-dot flow" aria-hidden="true" />
                    Live path
                </span>
            </div>
        </div>
    );
}
